import axios, { AxiosInstance } from "axios";
import axiosRetry, { isNetworkOrIdempotentRequestError } from "axios-retry";
import { useAuthStore } from "../stores/authStore";
import TokenService from "../service/tokenService";

const instance: AxiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:8080/api/v1",
    timeout: 30000,
    headers: {
        "client-id": "toffee-ui",
        "Content-Type": "application/json"
    },
});

axiosRetry(instance, {
    retries: 3,
    retryDelay: (retryCount, error) => {
        return axiosRetry.exponentialDelay(retryCount, error, 1000);
    },
    retryCondition: (error) => {
        if (isNetworkOrIdempotentRequestError(error)) return true;
        if (error.response.status === 401 && error.config.url !== '/account/token') return true;
        return false;
    },
    onRetry: async (retryCount, error, requestConfig) => {
        console.warn(`retring: ${requestConfig.url} count: ${retryCount} errorCode: ${error.code} errorStatus: ${error.status}`);
        if (error.status === 401) {
            if (TokenService.isRefreshing) {
                const newToken = await new Promise<string>((resolve) => {
                    TokenService.subscribeTokenRefresh((token: string) => {
                        resolve(token);
                    });
                });
                requestConfig.headers["Authorization"] = `Bearer ${newToken}`;
                return;
            }

            try {
                TokenService.isRefreshing = true;
                const newToken = await TokenService.refreshToken();
                if (newToken) {
                    TokenService.onTokenRefreshed(newToken);
                    requestConfig.headers["Authorization"] = `Bearer ${newToken}`;
                    return;
                }
            } catch (error) {
                console.error(`refresh token failed: ${error}`);
                useAuthStore.getState().clearAuth();
                window.location.href = '/login';
            } finally {
                TokenService.isRefreshing = false;
            }
        }
    },
});

instance.interceptors.request.use(config => {
    const idToken = useAuthStore.getState().idToken;
    if (idToken) {
        config.headers["Authorization"] = `Bearer ${idToken.SS}`;
    }
    return config;
});

instance.interceptors.response.use(response => {
    return response;
}, error => {
    return Promise.reject(error);
});

export default instance;